import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

interface PaidFeatureUpsellProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

/**
 * Shown in place of a feature that is gated behind a paid plan. Users with billing access get an
 * upgrade CTA that opens the plan modal on the billing page; everyone else is told to contact their
 * administrator.
 */
export const PaidFeatureUpsell = ({ icon, title, description }: PaidFeatureUpsellProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );

  const handleUpgrade = useCallback(() => {
    trackEvent(EventName.ClickUpgradePlan);
    navigate(`/${slug}/account/billing`, { state: { shouldTriggerAction: Date.now() } });
  }, [slug, navigate]);

  return (
    <div className={TABLE_LAYOUT_FILL_CLASSES}>
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">{icon}</EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {canReadBilling ? (
            <Button size="sm" onClick={handleUpgrade}>
              {t('billing.upgrade')}
            </Button>
          ) : (
            <p className="text-sm text-foreground-muted">{t('billing.contact_admin_to_upgrade')}</p>
          )}
        </EmptyContent>
      </Empty>
    </div>
  );
};
